/**
 * Returns a delayed-element-removal helper COMPILED IN THE GIVEN DOCUMENT'S OWN REALM,
 * or null when it cannot be installed (cross-origin / CSP without inline scripts).
 *
 * Why this exists: modals live in the TOP document while the code that closes them
 * often runs inside an iframe. Chrome invalidates every callback belonging to a
 * destroyed realm — even when the timer was registered on the surviving window
 * (verified empirically: `parent.setTimeout(fnFromIframe, 300)` never fires once the
 * iframe navigates/commits before the delay elapses). The only reliable way to run
 * delayed cleanup that survives the caller's death is a function whose code was
 * compiled in the target realm itself, which is what the injected inline script does.
 *
 * @param {Document} doc - Document that owns the element to remove later.
 * @returns {?function(string, number): void} `(elementId, delayMs)` helper or null.
 */
function targetRealmRemoveLater(doc) {
    try {
        const win = doc.defaultView;
        if (!win) { return null; }
        if (!win.__dcmsRemoveLater) {
            const s = doc.createElement('script');
            s.textContent = 'window.__dcmsRemoveLater=function(id,ms){setTimeout(function(){'
                + 'var e=document.getElementById(id);if(e){e.remove();}},ms);};';
            (doc.head || doc.documentElement).appendChild(s);
            s.remove();
        }
        return win.__dcmsRemoveLater || null;
    } catch (e) {
        return null;
    }
}

/**
 * Header styles of iziModal windows — global_settings `modal_header_style`, rendered by
 * views/DCMS/main.tpl as `<html data-dcms-modal-header>` (PHP: ModalHeaderStyle).
 * `dark` and `light` are painted by assets/css/modals.css; `classic` keeps the historical solid bar.
 */
const MODAL_HEADER_STYLES = ['dark', 'light', 'classic'];
const MODAL_HEADER_DEFAULT_STYLE = 'dark';

/** Window corner radius of the `dark` / `light` styles (iziModal option `radius`, restored after fullscreen) */
const MODAL_HEADER_RADIUS = { dark: 10, light: 12 };

export class Modals {
    /**
     * Initializes the Modals class.
     */
    constructor() {
        this.modalCounter = 0;
        this.spinnerModalId = 'spinner-modal';
        this.alertCounter = 0;
        this.alertQueue = [];
        this.isAlertShowing = false;
        this.init();
    }

    /**
     * Initializes the Modals class.
     */
    init() {
        this.targetWindow = this.findHighestDCMS2Parent();
        this.targetDocument = this.targetWindow.document;
		if (!this.targetDocument.modals)
			this.targetDocument.modals = new Map();
        this.$targetWindow = this.targetWindow.$;
        this.observeDarkMode();
        this.calculateScrollbarWidth();
      	this.initializeIziModals();
        DCMSDebugLog('initialized',3,'Modals');
    }
    
    /**
     * Closes the most recently opened modal.
     */
    closeCurrentModal() {
        const lastModalId = Array.from(this.targetDocument.modals.keys()).pop();
        if (lastModalId) {
            this.$targetWindow(`#${lastModalId}`).iziModal('close');
        }
    }
    
    /**
     * Closes the specified modal.
     * @param {string} modalId - ID of the modal to close.
     */
    closeModal(modalId) {
        this.$targetWindow(`#${modalId}`).iziModal('close');
    }

    /**
     * Header style of the portal, read from the document the windows open in.
     * @returns {string} 'dark' | 'light' | 'classic' (missing or unknown attribute = 'dark')
     */
    getModalHeaderStyle() {
        const value = this.targetDocument.documentElement.getAttribute('data-dcms-modal-header');
        return MODAL_HEADER_STYLES.includes(value) ? value : MODAL_HEADER_DEFAULT_STYLE;
    }

    /**
     * Applies the portal's header style to the options of an iziModal window, before
     * the window is initialised (iziModal remembers the element classes at init).
     *
     * A window with its own `headerColor` keeps it, its `borderBottom` and its `radius` —
     * callers that chose a colour are not overridden. Otherwise:
     *  - `classic` — the historical solid bar in `legacyColor` with the 3 px border under the window,
     *  - `dark` / `light` — the header is painted by assets/css/modals.css through
     *    `var(--dcms-izimodal-header-bg)` (the element gets `.dcms-izimodal--{style}`), no border
     *    under the window, rounded corners unless the caller set `radius`.
     *
     * @param {Object} options - iziModal options (modified in place).
     * @param {?HTMLElement} modalElement - Element the window is initialised on.
     * @param {string} legacyColor - Colour of the classic bar for this entry point.
     * @returns {Object} The same options.
     */
    applyHeaderStyle(options, modalElement, legacyColor) {
        if (options.headerColor) {
            return options;
        }
        const style = this.getModalHeaderStyle();
        const unset = (value) => value === undefined || value === null;
        if (style === 'classic') {
            options.headerColor = legacyColor;
            if (unset(options.borderBottom)) options.borderBottom = true;
            if (unset(options.radius)) options.radius = 3;
            return options;
        }
        options.headerColor = 'var(--dcms-izimodal-header-bg)';
        if (unset(options.borderBottom)) options.borderBottom = false;
        if (unset(options.radius)) options.radius = MODAL_HEADER_RADIUS[style];
        if (modalElement) {
            modalElement.classList.add('dcms-izimodal', `dcms-izimodal--${style}`);
        }
        return options;
    }

    /**
     * Removes an orphaned page-loading overlay left behind by a modal iframe.
     *
     * A form submitted inside a modal iframe paints #dcmsPageLoadingOverlay in the
     * host document (dcms-loader beforeunload). The overlay is normally removed by
     * the NEXT page's DOMContentLoaded — but when the response closes the modal,
     * the iframe dies before that ever runs and no 'hide' arrives, so the overlay
     * would hang until the 15 s watchdog. Called from every modal teardown.
     */
    removeOrphanedLoadingOverlay() {
        const overlay = this.targetDocument.getElementById('dcmsPageLoadingOverlay');
        if (overlay) {
            overlay.remove();
        }
    }

    /**
     * Calculates the scrollbar width.
     */
    calculateScrollbarWidth() {
        const scrollDiv = this.targetDocument.createElement("div");
        scrollDiv.style.width = '100px';
        scrollDiv.style.height = '100px';
        scrollDiv.style.overflow = 'scroll';
        scrollDiv.style.position = 'absolute';
        scrollDiv.style.top = '-9999px';
        this.targetDocument.body.appendChild(scrollDiv);
        
        this.scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
        
        this.targetDocument.body.removeChild(scrollDiv);
    }

  /**
     * Finds the highest parent iframe with a DCMS2 object.
     * @returns {Window} Highest parent window with DCMS2.
     */
    findHighestDCMS2Parent() {
        let currentWindow = window;
        let highestParent = currentWindow;

        while (currentWindow !== window.top) {
            currentWindow = currentWindow.parent;
            if (currentWindow.DCMS2) {
                highestParent = currentWindow;
            }
        }

        return highestParent;
    }
    
    /**
     * Observes dark mode changes.
     */
    observeDarkMode() {
        const observer = new MutationObserver(() => {
            this.syncDarkMode();
        });
        observer.observe(this.targetDocument.documentElement, { attributes: true, attributeFilter: ['class'] });
    }

    /**
     * Synchronizes dark mode across all modals.
     */
    syncDarkMode() {
        const isDark = this.targetDocument.documentElement.classList.contains('dark');
        this.targetDocument.modals.forEach((modal, id) => {
            const modalElement = this.$targetWindow(`#${id}`);
            if (modalElement.length) {
                modalElement.iziModal('setTransitionIn', isDark ? 'fadeInDown' : 'fadeInUp');
                modalElement.iziModal('setTransitionOut', isDark ? 'fadeOutDown' : 'fadeOutUp');
                const modalContent = modalElement.find('iframe').contents();
                if (modalContent.length) {
                    modalContent.find('html')[isDark ? 'addClass' : 'removeClass']('dark');
                }
            }
        });
    }

   /**
     * Displays a full-screen popup.
     * @param {string} html - HTML content of the popup.
     * @param {string} title - Popup title.
     * @param {Object} events - Object with event handler functions.
     * @param {boolean} isModal - Whether the popup should be modal.
     * @param {string|null} iframeURL - URL for the iframe (optional).
     * @returns {string} ID of the created modal.
     */
    showFullScreenPopup(html, title, events = {}, isModal = false, iframeURL = null, customModalId = null) {
        //const modalId = `dcms2-modal-${++this.modalCounter}`;
		let modalId = customModalId;
		if (!modalId) modalId = `dcms2-modal-${++this.modalCounter}-${Date.now()}`;
        const modalDiv = this.targetDocument.createElement('div');
        modalDiv.id = modalId;
        this.targetDocument.body.appendChild(modalDiv);

        this.hideScrollbar();

        const modalOptions = {
            title: this.toHeaderText(title),
            padding: 0,
            radius: 0,
            width: '100%',
            height: '100vh',
            top: 0,
            bottom: 0,
            closeButton: true,
            overlayClose: !isModal,
            fullscreen: true,
            closeOnEscape: !isModal,
            onOpening: (modal) => {
                const wrapper = this.targetDocument.createElement('div');
                wrapper.style.width = '100%';
                wrapper.style.height = '100%';
                wrapper.style.overflow = 'hidden';

                const iframe = this.targetDocument.createElement('iframe');
                if (iframeURL) {
                    iframe.src = iframeURL;
                } else {
                    iframe.srcdoc = decodeURIComponent(html.replace(/\+/g, ' '));
                }
                iframe.style.width = '100%';
                iframe.style.height = 'calc(100vh - 50px)';
                iframe.style.border = 'none';

				// Store a reference to the iframe
				this.targetDocument.modals.set(modalId, { 
				    options: modalOptions, 
				    events: events,
				    iframe: iframe  // Add a reference to the iframe
				});
				//console.log(this.targetDocument.modals);            
                wrapper.appendChild(iframe);
                modal.setContent(wrapper.outerHTML);

                // Loading background over the frame (not an iziModal `iframe` window, so the loader is
                // told directly); the instance of the top window keeps its timers alive when this realm dies
                const modalLoader = this.targetWindow.DCMS2 && this.targetWindow.DCMS2.modalLoader;
                const liveFrame = this.targetDocument.getElementById(modalId)?.querySelector('iframe');
                if (modalLoader && liveFrame) modalLoader.watchFrame(liveFrame);
            },
            onOpened: () => {
                this.syncDarkMode();
                if (events.onOpened) events.onOpened();
                
                const modalElement = this.targetDocument.getElementById(modalId);
                if (modalElement) {
                    modalElement.style.height = '100vh';
                    modalElement.style.maxHeight = 'none';
                    
                    const contentDiv = modalElement.querySelector('.iziModal-content');
                    if (contentDiv) {
                        contentDiv.style.height = 'calc(100vh - 50px)';
                        contentDiv.style.maxHeight = 'none';
                    }
                } else {
                    console.error('Nie znaleziono elementu modalu');
                }
            },
            onClosing: () => {
                // Notify the iframe that the modal is closing (so it doesn't show a spinner on beforeunload)
                const modalElement = this.targetDocument.getElementById(modalId);
                if (modalElement) {
                    const iframe = modalElement.querySelector('iframe');
                    if (iframe && iframe.contentWindow) {
                        try {
                            iframe.contentWindow.postMessage({ type: 'dcms-modal-closing' }, '*');
                        } catch (e) {
                            // Ignore cross-origin errors
                        }
                    }
                }
                if (events.onClosing) events.onClosing();
            },
            onClosed: () => {
                this.$targetWindow(`#${modalId}`).iziModal('destroy');
                this.targetDocument.body.removeChild(this.targetDocument.getElementById(modalId));
                this.targetDocument.modals.delete(modalId);
                this.removeOrphanedLoadingOverlay();
                this.showScrollbar();
                if (events.onClosed) events.onClosed();
            }
        };
		
        this.applyHeaderStyle(modalOptions, modalDiv, 'rgb(39, 39, 42)');
        this.$targetWindow(`#${modalId}`).iziModal(modalOptions);
        this.$targetWindow(`#${modalId}`).iziModal('open');

        return modalId;
    }

	/**
	 * Sends a message to modals
	 */
	sendMessageToModal(modalId, message) {
	    // Find the main modal div
	    const modalDiv = this.targetDocument.getElementById(modalId);
	    if (!modalDiv) {
	        console.warn('sendToModal: no such modal', modalId);
	        return;
	    }

	    // Find the iframe inside the modal
	    const iframe = modalDiv.querySelector('iframe');
	    if (!iframe || !iframe.contentWindow) {
	        console.warn('sendToModal: no iframe found in modal', modalId);
	        return;
	    }

	    // Send the message
	    console.log('sendToModal:', modalId, message);
	    iframe.contentWindow.postMessage(message, '*');
	}
		
    /**
     * Hides the scrollbar.
     */
    hideScrollbar() {
        DCMSDebugLog('hideScrollbar', 3, "modals");
        const body = this.targetDocument.body;
        const hasVerticalScrollbar = body.scrollHeight > body.clientHeight;
        
        body.style.overflow = 'hidden';
        if (hasVerticalScrollbar) {
            body.style.paddingRight = `${this.scrollbarWidth}px`;
        }
    }

    /**
     * Restores the scrollbar.
     */
    showScrollbar() {
        const body = this.targetDocument.body;
        body.style.overflow = '';
        body.style.paddingRight = '';
    }
    
/**
     * Displays a modal with a spinner.
     * @param {string} message - Message to display with the spinner.
     */
    showModalSpinner(message = 'Loading...') {
        const spinnerHtml = `
            <div id="${this.spinnerModalId}" class="modal-spinner">
                <div class="spinner-content">
                    <div class="spinner"></div>
                    <p>${message}</p>
                </div>
            </div>
        `;

        const spinnerStyles = `
            <style>
                .modal-spinner {
                    display: flex;
                    justify-content: center;
                    align-items: flex-start;
                    padding-top: 20vh;
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(0, 0, 0, 0.8);
                    z-index: 9999;
                }
                .spinner-content {
                    background-color: white;
                    padding: 30px 40px;
                    border-radius: 8px;
                    text-align: center;
                }
                :is(.dark .spinner-content) {
                    background-color: black;    
                }
                .spinner {
                    border: 5px solid #f3f3f3;
                    border-top: 5px solid #3498db;
                    border-radius: 50%;
                    width: 64px;
                    height: 64px;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 15px;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            </style>
        `;

        // Remove existing spinner, if present
        this.hideModalSpinner();

        // Add the new spinner to the DOM
        document.body.insertAdjacentHTML('beforeend', spinnerStyles + spinnerHtml);
    }

    /**
     * Hides the modal with the spinner.
     */
    hideModalSpinner() {
        const existingSpinner = document.getElementById(this.spinnerModalId);
        if (existingSpinner) {
            existingSpinner.remove();
        }
    }
   
    /**
     * Displays a modal alert.
     * @param {string} message - Alert content.
     * @param {string} informationStyle - Alert style ('Success', 'Warning', 'Error', 'SystemError', 'Neutral').
     * @param {string} customTitle - Custom alert title (optional).
     * @returns {Promise} Promise resolved after the alert is closed.
     */
    showModalAlert2(message, informationStyle = 'Neutral', customTitle = '') {
        return new Promise((resolve) => {
            this.alertQueue.push({ message, informationStyle, customTitle, resolve });
            this.processAlertQueue();
        });
    }

    /**
     * Processes the alert queue.
     */
    processAlertQueue() {
        if (this.isAlertShowing || this.alertQueue.length === 0) {
            return;
        }

        this.isAlertShowing = true;
        const { message, informationStyle, customTitle, resolve } = this.alertQueue.shift();
        this.showModalAlert(message, informationStyle, customTitle, resolve);
    }

    /**
     * Returns the default title for a given alert style.
     * @param {string} informationStyle - Alert style.
     * @returns {string} Default title.
     */
    getDefaultTitle(informationStyle) {
        switch(informationStyle) {
            case 'Success':
            case 'Neutral':
                return __i18n("Information");
            case 'Warning':
                return __i18n("Warning");
            case 'Error':
            case 'SystemError':
                return __i18n("Error");
            default:
                return __i18n("Information");
        }
    }
    
    /**
     * Returns styles for a given alert type.
     * @param {string} informationStyle - Alert style.
     * @returns {Object} Object with styles.
     */
	getAlertStyles(informationStyle) {
	    const styles = {
	        Success: {
	            bg: 'bg-green-100 dark:bg-green-800',
	            iconColor: 'text-green-500 dark:text-green-300',
	            textColor: 'text-green-700 dark:text-green-200',
	            iconName: 'check_circle'
	        },
	        Warning: {
	            bg: 'bg-yellow-100 dark:bg-yellow-800',
	            iconColor: 'text-yellow-500 dark:text-yellow-300',
	            textColor: 'text-yellow-700 dark:text-yellow-200',
	            iconName: 'warning'
	        },
	        Error: {
	            bg: 'bg-red-100 dark:bg-red-800',
	            iconColor: 'text-red-500 dark:text-red-300',
	            textColor: 'text-red-700 dark:text-red-200',
	            iconName: 'error'
	        },
	        SystemError: {
	            bg: 'bg-purple-100 dark:bg-purple-800',
	            iconColor: 'text-purple-500 dark:text-purple-300',
	            textColor: 'text-purple-700 dark:text-purple-200',
	            iconName: 'notification_important'
	        },
	        Neutral: {
	            bg: 'bg-blue-100 dark:bg-blue-800',
	            iconColor: 'text-blue-500 dark:text-blue-300',
	            textColor: 'text-blue-700 dark:text-blue-200',
	            iconName: 'info'
	        }
	    };
	
	    return styles[informationStyle] || styles.Neutral;
	}
	
	    /**
     * Creates HTML content for the modal.
     * @param {Object} options - Configuration options for the modal.
     * @param {string} options.id - Unique modal identifier.
     * @param {string} options.type - Modal type ('alert' or 'confirm').
     * @param {Object} options.styles - Object containing style classes.
     * @param {string} options.title - Modal title.
     * @param {string} options.message - Modal message content.
     * @param {string} options.confirmText - Confirm button text.
     * @param {string} options.cancelText - Cancel button text (confirm type only).
     * @returns {string} Modal HTML.
     */
    createModalContent(options) {
        const { id, type, styles, title, message, confirmText, cancelText } = options;

        return `
            <div id="${id}" class="modal-dialog opacity-0 transition-opacity duration-300">
                <div class="dialog-content ${styles.bg} p-6 mx-4 sm:mx-0 rounded-lg shadow-lg max-w-md w-full">
                    <div class="flex items-center mb-4 space-x-2">
                        <span class="material-symbols-rounded ${styles.iconColor} text-3xl">${styles.iconName}</span>
                        <div class="${styles.textColor} text-xl font-bold">${title}</div>
                    </div>
                    <div class="${styles.textColor} text-lg mb-4">${message}</div>
                    <hr class="border-t border-gray-300 dark:border-gray-600 my-4">
                    <div class="flex justify-end space-x-2">
                        ${type === 'confirm' ? `
                            <button class="cancel-btn px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">${cancelText}</button>
                        ` : ''}
                        <button class="confirm-btn px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700">${confirmText}</button>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Displays a modal (alert or confirm) and returns a Promise with the result.
     * @param {Object} options - Configuration options for the modal.
     * @param {string} options.type - Modal type ('alert' or 'confirm').
     * @param {string} options.message - Modal message content.
     * @param {string} options.informationStyle - Information style ('Success', 'Warning', 'Error', 'SystemError', 'Neutral').
     * @param {string} [options.customTitle] - Custom modal title (optional).
     * @returns {Promise<boolean>} Promise resolved with true (confirmed) or false (cancelled).
     */
    showModal(options) {
        return new Promise((resolve) => {
            const id = `modal-${options.type}-${++this.alertCounter}`;
            const styles = this.getAlertStyles(options.informationStyle);
            const title = this.toHeaderText(options.customTitle || this.getDefaultTitle(options.informationStyle));

            const modalHtml = this.createModalContent({
                id,
                type: options.type,
                styles,
                title,
                message: options.message,
                confirmText: __i18n("OK"),
                cancelText: __i18n("Cancel")
            });

            const modalStyles = `
                <style>
                .modal-dialog {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    z-index: 9999;
                }
                .dialog-content {
                    background-color: white;
                }
                :is(.dark .dialog-content) {
                    background-color: #1f2937;
                    color: white;
                }
                </style>
            `;

            // Add the new modal to the DOM
            this.targetDocument.body.insertAdjacentHTML('beforeend', modalStyles + modalHtml);

            const modalElement = this.targetDocument.getElementById(id);

            // Force reflow so the animation works
            modalElement.offsetHeight;

            // Show the modal with a fade-in effect
            // Timer scheduled on targetWindow, NOT on the calling window: the modal lives in the
            // TOP document while showModal() may run inside an iframe. Timers of an iframe that
            // navigates away are destroyed, which would strand the modal in its initial state.
            this.targetWindow.setTimeout(() => {
                modalElement.classList.remove('opacity-0');
            }, 10);

            // Add handling for buttons and the Enter/Escape keys
            const confirmBtn = modalElement.querySelector('.confirm-btn');
            const cancelBtn = modalElement.querySelector('.cancel-btn');

            const handleConfirm = () => {
                cleanup();
                this.hideModal(id);
                resolve(true);
            };

            const handleCancel = () => {
                cleanup();
                this.hideModal(id);
                resolve(false);
            };

            confirmBtn.addEventListener('click', handleConfirm);
            if (cancelBtn) {
                cancelBtn.addEventListener('click', handleCancel);
            }
            
            // 'keydown', NOT 'keypress': browsers never emit 'keypress' for Escape (the event
            // only fires for character-producing keys + Enter), so Escape-to-cancel silently
            // did not work when this listener used 'keypress'.
            const handleKeyPress = (event) => {
                if (event.key === 'Enter') {
                    handleConfirm();
                } else if (event.key === 'Escape' && options.type === 'confirm') {
                    handleCancel();
                }
            };
            this.targetDocument.addEventListener('keydown', handleKeyPress);

            // Detach the key listener when the modal closes. Called from handleConfirm /
            // handleCancel — previously this was dead code (`return cleanup` inside a Promise
            // executor is ignored), so every modal leaked its key handler on the target
            // document for the lifetime of the TOP window.
            const cleanup = () => {
                this.targetDocument.removeEventListener('keydown', handleKeyPress);
            };
        });
    }

    /**
     * Hides the modal with the given ID.
     * @param {string} modalId - ID of the modal to hide.
     */
    hideModal(modalId) {
        const modal = this.targetDocument.getElementById(modalId);
        if (modal) {
            modal.classList.add('opacity-0');
            // The delayed removal MUST run in the target document's own realm. hideModal()
            // often runs inside an iframe (e.g. a confirm dialog whose OK handler immediately
            // navigates that iframe) while the modal element lives in the TOP document. Any
            // callback from the iframe realm — even one registered via targetWindow.setTimeout —
            // is invalidated the moment the iframe's document is destroyed, leaving an invisible
            // full-viewport overlay (opacity-0, z-index 9999) in the TOP document forever,
            // blocking all clicks. See targetRealmRemoveLater().
            const removeLater = targetRealmRemoveLater(this.targetDocument);
            if (removeLater) {
                removeLater(modalId, 300);
            } else {
                // Helper unavailable (CSP) — best effort; correct whenever the caller survives.
                this.targetWindow.setTimeout(() => {
                    modal.remove();
                }, 300);
            }
        }
    }

    /**
     * Displays a modal alert.
     * @param {string} message - Alert content.
     * @param {string} [informationStyle='Neutral'] - Information style ('Success', 'Warning', 'Error', 'SystemError', 'Neutral').
     * @param {string} [customTitle] - Custom alert title (optional).
     * @returns {Promise<boolean>} Promise resolved after the alert is closed.
     */
    showModalAlert(message, informationStyle = 'Neutral', customTitle = '') {
        return this.showModal({
            type: 'alert',
            message,
            informationStyle,
            customTitle
        });
    }

    /**
     * Displays a confirmation dialog.
     * @param {string} message - Confirmation content.
     * @param {string} [informationStyle='Neutral'] - Information style ('Success', 'Warning', 'Error', 'SystemError', 'Neutral').
     * @param {string} [customTitle] - Custom confirmation title (optional).
     * @returns {Promise<boolean>} Promise resolved with true (confirmed) or false (cancelled).
     */
    showModalConfirm(message, informationStyle = 'Neutral', customTitle = '') {
        return this.showModal({
            type: 'confirm',
            message,
            informationStyle,
            customTitle
        });
    }
    
    /**
	 * Initializes iziModal handling for elements with the data-dcms-modal="izimodal" attribute.
	 * This method should be called in the constructor or the init method of the Modals class.
	 */
	initializeIziModals() {
		var that = this
		document.addEventListener('click', (e) => {
	        const element = e.target.closest('[data-dcms-modal="izimodal"]');
	        if (element) {
	            e.preventDefault();
	            that.openIziModal(element);
	        }
	    });
	}

	/**
	 * Opens an iziModal for the given element.
	 * @param {HTMLElement} element - DOM element that triggered opening the modal.
	 */
	openIziModal(element) {
	    // Every frame has its own Modals instance (and counter), but all of them append
	    // modals to the shared target document — skip ids already taken there, e.g. by
	    // the modal that hosts this frame (a duplicate id would re-target that modal)
	    let modalId;
	    do {
	        modalId = `dcms2-izimodal-${++this.modalCounter}`;
	    } while (this.targetDocument.getElementById(modalId) || this.targetDocument.modals.has(modalId));
	    const modalDiv = this.targetDocument.createElement('div');
	    modalDiv.id = modalId;
	    this.targetDocument.body.appendChild(modalDiv);
	
	    const options = this.getIziModalOptions(element, modalId);
	
	    this.$targetWindow(`#${modalId}`).iziModal(options);
	    this.targetDocument.modals.set(modalId, { options: options, element: element });
	    this.$targetWindow(`#${modalId}`).iziModal('open');
	}
	
	/**
	 * Retrieves iziModal configuration options from the element's attributes.
	 * @param {HTMLElement} element - DOM element containing configuration attributes.
	 * @param {string} modalId - Identifier of the modal being created.
	 * @returns {Object} Object with iziModal configuration options.
	 */
	getIziModalOptions(element, modalId) {
	    const options = {};
	    const attributes = [
	        'title', 'subtitle', 'headerColor', 'background', 'theme', 'icon', 'iconText', 'iconColor',
	        'rtl', 'width', 'top', 'bottom', 'borderBottom', 'padding', 'radius', 'zindex',
	        'iframe', 'iframeHeight', 'iframeURL', 'focusInput', 'group', 'loop', 'arrowKeys',
	        'navigateCaption', 'navigateArrows', 'history', 'restoreDefaultContent', 'autoOpen',
	        'bodyOverflow', 'fullscreen', 'openFullscreen', 'closeOnEscape', 'closeButton', 'appendTo',
	        'appendToOverlay', 'overlay', 'overlayClose', 'overlayColor', 'timeout', 'timeoutProgressbar',
	        'pauseOnHover', 'timeoutProgressbarColor', 'transitionIn', 'transitionOut',
	        'transitionInOverlay', 'transitionOutOverlay', 'strictModal', 'base64'
	    ];

	    const callbackEvents = [
	        'onFullscreen', 'onResize', 'onOpening', 'onOpened', 
	        'onClosing', 'onClosed', 'afterRender'
	    ];

	    attributes.forEach(attr => {
	        const value = element.getAttribute(`data-dcms-izimodal-${attr}`);
	        if (value !== null) {
	            options[attr] = this.parseAttributeValue(value);
	        }
	    });

	    // The header is plain text: getAttribute() returns the value unescaped and iziModal inserts it as HTML
	    ['title', 'subtitle'].forEach(attr => {
	        if (attr in options) options[attr] = this.toHeaderText(options[attr]);
	    });

		this.applyHeaderStyle(options, this.targetDocument.getElementById(modalId), 'rgb(0,0,0)');
		
	    if (options.strictModal === true) {
	        options.closeOnEscape = false;
	        options.closeButton = false;
	        options.overlayClose = false;
	        options.bodyOverflow = true;
	    }

	    if (!options.iframeURL && element.href && !options.base64) {
	        options.iframeURL = element.href;
	    }
	    
	    const userCallbacks = {};
	    callbackEvents.forEach(eventName => {
	        const callbackName = element.getAttribute(`data-dcms-izimodal-${eventName.toLowerCase()}`);
	        if (callbackName && typeof window[callbackName] === 'function') {
	            userCallbacks[eventName] = window[callbackName];
	        }
	    });
	    
	    const internalOnOpening = (modal) => {
	        this.hideScrollbar();
	        this.syncDarkMode();

	        // Expose active modal context for popup callbacks (license, etc.)
	        try {
	            const triggerWindow = element.ownerDocument?.defaultView || window;
	            window.top._dcmsActiveModal = {
	                element: element,
	                windowActive: triggerWindow,
	                close: () => this.closeCurrentModal()
	            };
	        } catch (e) { /* cross-origin */ }

	        // NEW, FIXED LOGIC FOR base64
	        if (options.base64) {
	            try {
                    // Step 1: Decode from Base64 to a binary string
                    const binaryString = atob(options.base64);
                    // Step 2: Convert the binary string to a byte array
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    // Step 3: Use TextDecoder to interpret the bytes as UTF-8
                    const decodedHtml = new TextDecoder('utf-8').decode(bytes);
	                modal.setContent(decodedHtml);
	            } catch (e) {
	                console.error("Błąd dekodowania base64 dla modala:", e);
	                modal.setContent("<div class='p-4'>Błąd podczas ładowania treści.</div>");
	            }
	        }
	    };

	    const internalOnClosing = () => {
	        // Notify the iframe that the modal is closing (so it doesn't show a spinner on beforeunload)
	        const modalElement = this.targetDocument.getElementById(modalId);
	        if (modalElement) {
	            const iframe = modalElement.querySelector('iframe');
	            if (iframe && iframe.contentWindow) {
	                try {
	                    iframe.contentWindow.postMessage({ type: 'dcms-modal-closing' }, '*');
	                } catch (e) {
	                    // Ignore cross-origin errors
	                }
	            }
	        }
	    };

	    const internalOnClosed = () => {
	        const modalElement = this.targetDocument.getElementById(modalId);
	        if (modalElement) {
	            this.$targetWindow(`#${modalId}`).iziModal('destroy');
	            modalElement.remove();
	            this.targetDocument.modals.delete(modalId);
	        }
	        this.removeOrphanedLoadingOverlay();
	        this.showScrollbar();
	    };

	    callbackEvents.forEach(eventName => {
	        const userCallback = userCallbacks[eventName];

	        if (eventName === 'onOpening') {
	            options.onOpening = (modal) => {
	                internalOnOpening(modal);
	                if (userCallback) userCallback(modal);
	            };
	        } else if (eventName === 'onClosing') {
	            options.onClosing = () => {
	                internalOnClosing();
	                if (userCallback) userCallback();
	            };
	        } else if (eventName === 'onClosed') {
	            options.onClosed = () => {
	                if (userCallback) userCallback();
	                internalOnClosed();
	            };
	        } else if (userCallback) {
	            options[eventName] = userCallback;
	        }
	    });

	    if (!options.onOpening) {
	        options.onOpening = internalOnOpening;
	    }
	    if (!options.onClosing) {
	        options.onClosing = internalOnClosing;
	    }
	    if (!options.onClosed) {
	        options.onClosed = internalOnClosed;
	    }

	    // Opt-in: the modal follows the height of its iframe content
	    if (options.iframe === true && element.getAttribute('data-dcms-izimodal-autoheight') === 'true') {
	        const onOpening = options.onOpening;
	        options.onOpening = (modal) => {
	            onOpening(modal);
	            this.fitIframeToContent(modalId);
	        };
	    }
	    
	    return options;
	}

	/**
	 * Keeps the iframe of an iziModal as tall as its content, up to the window height
	 * (opt-in: data-dcms-izimodal-autoheight="true"). The content is measured on every
	 * load of the iframe (a form submitted inside reloads it) and on every change of its
	 * size. iziModal re-applies options.iframeHeight every 300 ms, so the height goes
	 * through that option. A cross-origin page keeps the fixed iframeHeight.
	 * @param {string} modalId - Identifier of the modal.
	 */
	fitIframeToContent(modalId) {
	    const modalElement = this.targetDocument.getElementById(modalId);
	    const iframe = modalElement ? modalElement.querySelector('iframe') : null;
	    const instance = this.$targetWindow(`#${modalId}`).data('iziModal');
	    if (!iframe || !instance) return;

	    let observer = null;
	    const fit = () => {
	        let doc = null;
	        try {
	            doc = iframe.contentDocument;
	        } catch (e) {
	            return;
	        }
	        if (!doc || !doc.documentElement) return;
	        // documentElement.offsetHeight = height of the content, not of the iframe viewport
	        const height = Math.ceil(doc.documentElement.offsetHeight);
	        if (height > 0 && Math.abs(height - instance.options.iframeHeight) > 1) {
	            instance.options.iframeHeight = height;
	            instance.recalcLayout();
	        }
	    };

	    iframe.addEventListener('load', () => {
	        if (observer) observer.disconnect();
	        observer = null;
	        fit();
	        try {
	            const win = iframe.contentWindow;
	            if (win && typeof win.ResizeObserver === 'function') {
	                observer = new win.ResizeObserver(fit);
	                observer.observe(iframe.contentDocument.documentElement);
	            }
	        } catch (e) {
	            // cross-origin page — keep the fixed height
	        }
	    });
	}
	
	/**
	 * Parses an attribute value into the appropriate data type.
	 * @param {string} value - Attribute value to parse.
	 * @returns {boolean|number|string} Parsed value.
	 */
	parseAttributeValue(value) {
	    if (value === 'true') return true;
	    if (value === 'false') return false;
	    if (!isNaN(value) && value.trim() !== '') return Number(value);
	    return value;
	}

	/**
	 * Converts a modal header value (title, subtitle) to escaped plain text.
	 * HTML entities are decoded and tags are dropped, so a value escaped by the caller
	 * and an unescaped one display the same text. The text is escaped again because
	 * iziModal and createModalContent() insert the header as HTML.
	 * @param {*} value - Header value.
	 * @returns {*} Escaped text; non-string values (null, undefined, numbers, booleans) unchanged.
	 */
	toHeaderText(value) {
	    if (typeof value !== 'string') return value;
	    // DOMParser creates an inert document: scripts do not run and resources are not loaded
	    const body = new DOMParser().parseFromString(value, 'text/html').body;
	    // A line break becomes a space, so the words around it do not run together
	    body.querySelectorAll('br').forEach(br => br.replaceWith(' '));
	    const text = body.textContent || '';
	    const span = document.createElement('span');
	    span.textContent = text;
	    return span.innerHTML;
	}

/**
 * iziModal parameter description:
 * 
 * @param {string} title - Modal title.
 * @param {string} subtitle - Modal subtitle.
 * @param {string} headerColor - Modal header color.
 * @param {string} background - Modal background color.
 * @param {string} theme - Modal theme ('light' or 'dark').
 * @param {string} icon - Icon class to display in the header.
 * @param {string} iconText - Text to display as an icon.
 * @param {string} iconColor - Icon color.
 * @param {boolean} rtl - Whether to use right-to-left direction.
 * @param {string} width - Modal width.
 * @param {string} top - Distance from the top.
 * @param {string} bottom - Distance from the bottom.
 * @param {boolean} borderBottom - Whether to display the bottom border.
 * @param {string} padding - Modal inner padding.
 * @param {string} radius - Modal corner radius.
 * @param {number} zindex - z-index value for the modal.
 * @param {boolean} iframe - Whether to load content as an iframe.
 * @param {string} iframeHeight - Iframe height.
 * @param {string} iframeURL - URL to load in the iframe.
 * @param {boolean} focusInput - Whether to automatically focus the first input field.
 * @param {string} group - Group name for related modals.
 * @param {boolean} loop - Whether to loop navigation within the modal group.
 * @param {boolean} arrowKeys - Whether to use arrow keys for navigation.
 * @param {boolean} navigateCaption - Whether to display navigation captions.
 * @param {string} navigateArrows - Navigation arrows display mode.
 * @param {boolean} history - Whether to add entries to browser history.
 * @param {boolean} restoreDefaultContent - Whether to restore default content after closing.
 * @param {boolean} autoOpen - Whether to automatically open the modal.
 * @param {boolean} bodyOverflow - Whether to block body scrolling.
 * @param {boolean} fullscreen - Whether to allow fullscreen mode.
 * @param {boolean} openFullscreen - Whether to open directly in fullscreen mode.
 * @param {boolean} closeOnEscape - Whether to close the modal when the Escape key is pressed.
 * @param {boolean} closeButton - Whether to display the close button.
 * @param {string} appendTo - Selector of the element the modal should be appended to.
 * @param {string} appendToOverlay - Selector of the element the overlay layer should be appended to.
 * @param {boolean} overlay - Whether to display the overlay layer.
 * @param {boolean} overlayClose - Whether to close the modal when clicking the overlay.
 * @param {string} overlayColor - Overlay layer color.
 * @param {number} timeout - Time in milliseconds after which the modal should close automatically.
 * @param {boolean} timeoutProgressbar - Whether to display a progress bar for the timeout.
 * @param {boolean} pauseOnHover - Whether to pause the timeout on mouse hover.
 * @param {string} timeoutProgressbarColor - Timeout progress bar color.
 * @param {string} transitionIn - Modal entry animation.
 * @param {string} transitionOut - Modal exit animation.
 * @param {string} transitionInOverlay - Overlay entry animation.
 * @param {string} transitionOutOverlay - Overlay exit animation.
 * @param {boolean} strictModal - Whether to use strict modal mode (blocks closing).
 * @param {string} base64 - Base64-encoded HTML content of the modal.
 */

}


// ═══════════════════════════════════════════════════════════════════
// DCMSPopup — lightweight vanilla JS popup (no jQuery/iziModal)
// ═══════════════════════════════════════════════════════════════════

/**
 * Lightweight popup rendered in the parent window (top frame).
 * Requires no jQuery, Tailwind, or any external libraries.
 * Styled inline, supports dark mode, ESC, click-outside.
 *
 * Usage:
 *   var popup = new DCMSPopup({
 *       title: 'Ustawienia',
 *       body: document.createElement('div'),  // or HTML string
 *       buttons: [
 *           { label: 'Anuluj', style: 'secondary', onClick: function() {} },
 *           { label: 'Zapisz', style: 'primary', onClick: function() {} }
 *       ]
 *   });
 *   popup.open();
 */
export class DCMSPopup {
    static _counter = 0;

    /**
     * @param {Object} opts
     * @param {string} opts.title - Header title
     * @param {string|HTMLElement} [opts.body=''] - Content (HTML string or DOM element)
     * @param {Array<{label:string, onClick:Function, style?:string, icon?:string}>} [opts.buttons=[]] - Footer buttons (style: 'primary'|'secondary'|'danger'; icon: Material Symbols Rounded name, e.g. 'save', 'delete', 'undo')
     * @param {string} [opts.width='420px'] - Panel width
     * @param {string} [opts.maxHeight='70vh'] - Max height
     * @param {boolean} [opts.overlayClose=true] - Close by clicking the overlay
     * @param {boolean} [opts.escapeClose=true] - Close with ESC
     * @param {Function} [opts.onClose] - Callback after closing
     * @param {Document} [opts.targetDocument] - Target document (default: top window)
     */
    constructor(opts) {
        this.opts = Object.assign({
            title: '',
            body: '',
            buttons: [],
            width: '420px',
            maxHeight: '70vh',
            overlayClose: true,
            escapeClose: true,
            onClose: null,
            targetDocument: null
        }, opts);

        this._id = 'dcms-popup-' + (++DCMSPopup._counter) + '-' + Date.now();
        this._doc = this.opts.targetDocument || DCMSPopup.getTargetDocument();
        this._overlay = null;
        this._bodyEl = null;
        this._onEsc = null;
    }

    /**
     * Opens the popup — creates the DOM, fades in.
     * @returns {DCMSPopup} this (for chaining)
     */
    open() {
        var doc = this._doc;
        var isDark = DCMSPopup.isDarkMode(doc);
        var c = DCMSPopup.getColors(isDark);

        // Remove existing overlay with the same ID
        var existing = doc.getElementById(this._id);
        if (existing) existing.remove();

        // --- Overlay ---
        var overlay = doc.createElement('div');
        overlay.id = this._id;
        overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;display:flex;justify-content:center;align-items:center;'
            + 'background:rgba(0,0,0,0.3);opacity:0;transition:opacity 0.2s ease';
        if (this.opts.overlayClose) {
            var self = this;
            overlay.addEventListener('mousedown', function(e) {
                if (e.target === overlay) self.close();
            });
        }
        this._overlay = overlay;

        // --- Panel ---
        var panel = doc.createElement('div');
        panel.style.cssText = 'border-radius:12px;box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);'
            + 'width:' + this.opts.width + ';max-height:' + this.opts.maxHeight + ';display:flex;flex-direction:column;overflow:hidden;'
            + 'background:' + c.panelBg + ';color:' + c.text;

        // --- Header ---
        var header = doc.createElement('div');
        header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:14px 16px;'
            + 'border-bottom:1px solid ' + c.border;
        var titleEl = doc.createElement('h3');
        titleEl.textContent = this.opts.title;
        titleEl.style.cssText = 'margin:0;font-size:15px;font-weight:600';
        header.appendChild(titleEl);

        var closeBtn = doc.createElement('button');
        closeBtn.type = 'button';
        closeBtn.style.cssText = 'border:none;background:transparent;cursor:pointer;padding:4px;border-radius:6px;color:' + c.muted;
        closeBtn.innerHTML = '<span class="material-symbols-rounded" style="font-size:20px">close</span>';
        var self = this;
        closeBtn.addEventListener('click', function() { self.close(); });
        header.appendChild(closeBtn);
        panel.appendChild(header);

        // --- Body ---
        var bodyContainer = doc.createElement('div');
        bodyContainer.style.cssText = 'overflow-y:auto;flex:1;padding:12px 16px';

        if (typeof this.opts.body === 'string') {
            bodyContainer.innerHTML = this.opts.body;
        } else if (this.opts.body && this.opts.body.nodeType === 1) {
            // Cross-document: adoptNode if the element comes from a different document (e.g. iframe)
            var bodyNode = this.opts.body;
            if (bodyNode.ownerDocument !== doc) {
                bodyNode = doc.adoptNode(bodyNode);
            }
            bodyContainer.appendChild(bodyNode);
        }
        this._bodyEl = bodyContainer;
        panel.appendChild(bodyContainer);

        // --- Footer (only when there are buttons) ---
        if (this.opts.buttons.length > 0) {
            var footer = doc.createElement('div');
            footer.style.cssText = 'display:flex;justify-content:space-between;padding:12px 16px;gap:8px;'
                + 'border-top:1px solid ' + c.border;

            // If 1 button — justify-end, if 2+ — space-between
            if (this.opts.buttons.length === 1) {
                footer.style.justifyContent = 'flex-end';
            }

            this.opts.buttons.forEach(function(btn) {
                var el = doc.createElement('button');
                el.type = 'button';

                // Optional icon (Material Symbols Rounded) before the label.
                // The `material-symbols-rounded` class is standard in DCMS.
                if (btn.icon) {
                    var iconEl = doc.createElement('i');
                    iconEl.className = 'material-symbols-rounded';
                    iconEl.style.cssText = 'font-size:18px;vertical-align:middle;margin-right:6px;line-height:1';
                    iconEl.textContent = btn.icon;
                    el.appendChild(iconEl);
                    var labelSpan = doc.createElement('span');
                    labelSpan.textContent = btn.label;
                    labelSpan.style.verticalAlign = 'middle';
                    el.appendChild(labelSpan);
                } else {
                    el.textContent = btn.label;
                }

                var btnStyle = btn.style || 'secondary';
                if (btnStyle === 'primary') {
                    el.style.cssText = 'padding:6px 16px;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;'
                        + 'border:none;background:#6366f1;color:#ffffff';
                } else if (btnStyle === 'danger') {
                    el.style.cssText = 'padding:6px 16px;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;'
                        + 'border:none;background:#ef4444;color:#ffffff';
                } else {
                    el.style.cssText = 'padding:6px 16px;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;'
                        + 'border:1px solid ' + c.btnBorder + ';background:transparent;color:' + c.muted;
                }

                el.addEventListener('click', function() {
                    if (btn.onClick) btn.onClick(self);
                });
                footer.appendChild(el);
            });
            panel.appendChild(footer);
        }

        overlay.appendChild(panel);
        doc.body.appendChild(overlay);

        // Fade in
        requestAnimationFrame(function() { overlay.style.opacity = '1'; });

        // ESC
        if (this.opts.escapeClose) {
            this._onEsc = function(e) { if (e.key === 'Escape') self.close(); };
            doc.addEventListener('keydown', this._onEsc);
        }

        return this;
    }

    /**
     * Closes the popup — fade-out, cleanup DOM.
     */
    close() {
        if (!this._overlay) return;
        var overlay = this._overlay;
        var doc = this._doc;
        var onEsc = this._onEsc;
        var onClose = this.opts.onClose;

        overlay.style.opacity = '0';
        // Delayed removal must be compiled in the overlay document's own realm — a callback
        // from this (possibly iframe) realm dies with it, even when registered on the target
        // window's timer. See targetRealmRemoveLater().
        const removeLater = targetRealmRemoveLater(doc);
        if (removeLater) {
            removeLater(this._id, 200);
        } else {
            (doc.defaultView || window).setTimeout(function() { overlay.remove(); }, 200);
        }

        if (onEsc) {
            doc.removeEventListener('keydown', onEsc);
        }
        this._overlay = null;
        this._bodyEl = null;
        this._onEsc = null;

        if (typeof onClose === 'function') onClose();
    }

    /**
     * Returns the popup body content DOM element.
     * Use after open() to dynamically add elements.
     * @returns {HTMLElement|null}
     */
    getBodyElement() {
        return this._bodyEl;
    }

    /**
     * Returns the target document (top window with DCMS2).
     * @returns {Document}
     */
    static getTargetDocument() {
        try {
            var w = window;
            while (w !== w.top) w = w.parent;
            return w.document;
        } catch(e) { return document; }
    }

    /**
     * Checks whether dark mode is active.
     * @param {Document} [doc] - optional document (default: top window)
     * @returns {boolean}
     */
    static isDarkMode(doc) {
        try {
            if (!doc) {
                var w = window;
                while (w !== w.top) { w = w.parent; }
                doc = w.document;
            }
            return doc.documentElement.classList.contains('dark');
        } catch(e) {
            return document.documentElement.classList.contains('dark');
        }
    }

    /**
     * Returns the color palette (inline CSS values) for dark/light mode.
     * @param {boolean} isDark
     * @returns {Object}
     */
    static getColors(isDark) {
        return {
            panelBg:    isDark ? '#2d2d30' : '#ffffff',
            text:       isDark ? '#e4e4e7' : '#111827',
            border:     isDark ? '#404043' : '#e5e7eb',
            muted:      isDark ? '#71717a' : '#9ca3af',
            btnBorder:  isDark ? '#3f3f46' : '#d1d5db',
            inputBg:    isDark ? '#18181b' : '#ffffff',
            inputBorder:isDark ? '#3f3f46' : '#d1d5db',
            inputText:  isDark ? '#e4e4e7' : '#111827',
            rowBg:      isDark ? '#27272a' : '#f9fafb',
            rowBorder:  isDark ? '#3f3f46' : '#e5e7eb',
            accent:     isDark ? '#a78bfa' : '#6366f1',
            mutedHover: isDark ? '#a1a1aa' : '#6b7280'
        };
    }
}